import React from 'react';

import _ from 'lodash';

import { Popover } from '@blueprintjs/core';
import { ActionIcon, Avatar, Box, Group } from '@good/ui/core';

import { Text } from 'common-components/typography';
import { ActionMenu, ActionMenuItem } from 'common-components/action-menu';
import { IRosterBooking, IRosterCustomer } from '../../types';

import { BookingStatus } from 'utilities/enum-utils';
import CommonUtils from 'utilities/common-utils';
import { useHistory } from 'react-router-dom';
import { MoreHoriz } from '@good/icons';
import { rosterConfig } from '../../roster-config';

interface CustomerContentRowProps {
  customer: IRosterCustomer;
  bookings?: IRosterBooking[][];
}

const { TIMELINE_ROW_HEIGHT } = rosterConfig;

export function CustomersColumn({ customer, bookings }: CustomerContentRowProps) {
  const history = useHistory();
  const { customerFirstName, customerLastName, customerAvatarUrl, customerId } = customer;
  const customerName = `${customerFirstName} ${customerLastName}`;
  const rowHeight = _.isEmpty(bookings) ? TIMELINE_ROW_HEIGHT : TIMELINE_ROW_HEIGHT * _.size(bookings);
  const revenueAmount = calculateCostsForBookings(bookings);

  const goToProfile = (customerId: string) => {
    history.push(`/customer/details/${customerId}`);
  };

  return (
    <Box className='roster-gray bordered-right bordered-bottom anim-fade-in-fast' style={{ height: rowHeight }}>
      <Group justify='space-between' gap='xs' h='100%' wrap='nowrap' w='100%' px='lg'>
        <div className='flex-row align-center'>
          <Avatar mr='sm' src={customerAvatarUrl} name={customerName} />
          <div className='line-height-135 flex-column'>
            <div className='whitespace-nowrap overflow-hidden text-overflow-ellipsis'>
              <Text lineHeight={120}>{customerName}</Text>
            </div>
            <div className='whitespace-nowrap overflow-hidden text-overflow-ellipsis'>
              <Text size='regular' color='secondary' lineHeight={120}>
                {CommonUtils.formatPrice(revenueAmount)}
              </Text>
            </div>
          </div>
        </div>
        <Popover
          content={
            <ActionMenu>
              <ActionMenuItem text={'View customer profile'} onClick={() => goToProfile(customerId)} />
            </ActionMenu>
          }
          position='bottom-left'
        >
          <ActionIcon variant='default'>
            <MoreHoriz />
          </ActionIcon>
        </Popover>
      </Group>
    </Box>
  );
}

function calculateCostsForBookings(bookings) {
  const flattenedBookings = _.chain(bookings)
    .flatten()
    .filter(
      (booking) =>
        !_.includes(booking.bookingStatus, [
          BookingStatus.CUSTOMER_CANCELLED,
          BookingStatus.CUSTOMER_CANCELLED_WITHOUT_FEE,
          BookingStatus.BUSINESS_CANCELLED,
        ]),
    )
    .value();

  return _.reduce(
    flattenedBookings,
    (acc, booking: IRosterBooking) => {
      const { total } = booking;
      return acc + Number(total);
    },
    0,
  );
}
